<!--For published assessments -->
<template>
  <div class="container">
    <Loading v-show="!assessment" />
    <!--Draft-->
    <div v-if="assessment != null">

      <div v-if="assessment.isDraft">
        <div class="w-full mb-6">
          <h1 class="mb-2 font-serif font-semibold text-hlg">{{ assessment.heading }}</h1>
        </div>

        <!-- Toolbar -->
        <Toolbar v-if="assessment" :model="assessment" :showNotify="false" :showSharing="true" :showPrint="true"
          :hideContact="assessment.hideContact" class="border-b border-gray-100 pb-9 mb-9" />

        <!--Summary-->
        <section v-if="assessment.summary" class="mb-9">
          <div v-html="assessment.summary" class="article-small"></div>

          <!--Link list-->
          <div v-if="assessment.linkList && assessment.linkList.length > 0" class="article-small">
            <h2>Lenker</h2>
            <ul>
              <li v-for="link in assessment.linkList" :key="link.url">
                <p class="mb-6 text-xl md:text-2xl font-medium transition-all">
                  <a :href="link.url">{{ link.text }}</a>
                </p>
              </li>
            </ul>
          </div>
        </section>
        <!--Detail List-->
        <div class="w-full">
          <DetailList :organizations="assessment.relatedOrganizations"
            :municipalities="assessment.relatedMunicipalities" />
        </div>
        <div v-show="assessment.approvedDisplayContact" class="w-full pb-6 md:w-1/2">
          <p class="pb-1 font-sans text-sm text-gray-300">{{ $t('schema.schemaPage.contact') }}:</p>
          <!--TODO:GDPR-->
          <p v-if="assessment.approvedDisplayContact"><a :href="contactEmail"
              class="underline cursor-pointer text-lightblue-300">{{ contactName }}</a></p>
        </div>

      </div>
      <!--Published-->
      <div v-else class="w-full">

        <!-- Header -->
        <div class="flex justify-center mb-4">
          <div class="flex w-full justify-end xl:w-3/4">
            <div class="w-full xl:w-3/4">
              <h1 class="mb-6 font-serif font-semibold text-hmd md:text-hlg">
                {{ assessment.heading }}
              </h1>
              <ChangeLogSimple v-if="assessment" class="mb-6" :model="assessment" />
              <!--Toolbar-->
              <Toolbar v-if="assessment" :model="assessment" :showSharing="true" :showPrintableVersionLink="true"
                :hideContact="assessment.hideContact" class="pb-4 mb-4" />

              <!-- Assessment as PDF -->
              <section v-if="assessment.schemaAsPdf" class="pb-8 mt-12 mb-8 border-b border-gray-100 print:hidden">
                <a :href="assessment.schemaAsPdf" class="text-lg md:text-hxs text-lightblue-300 group"
                  :title="$t('schema.schemaPage.downloadMethodEvaluation')" download>
                  <font-awesome-icon class="mr-4" icon="file-pdf" />
                  <span class="underline group-hover:no-underline">
                    {{ $t('schema.schemaPage.downloadMethodEvaluation') }}
                  </span>
                </a>
              </section>

              <!--Summary-->
              <section v-if="assessment.summary" class="mb-9">
                <div v-html="assessment.summary" class="article-small"></div>
                <!--Link list-->
                <div v-if="assessment.linkList && assessment.linkList.length > 0" class="article-small">
                  <h2>Lenker</h2>
                  <ul>
                    <li v-for="link in assessment.linkList" :key="link.url">
                      <p class="mb-6 text-xl md:text-2xl font-medium transition-all">
                        <a :href="link.url">{{ link.text }}</a>
                      </p>
                    </li>
                  </ul>
                </div>
              </section>
              <!--Detail List-->
              <div class="w-full">
                <DetailList :organizations="assessment.relatedOrganizations"
                  :municipalities="assessment.relatedMunicipalities" />
              </div>
            </div>
          </div>
        </div>
        <!-- End header-->


        <!-- Content -->
        <div v-if="assessmentSchema && assessmentSchema.schemaItems && assessmentSchema.schemaItems.length > 0"
          class="articlebg py-12">
          <!-- Mobile tab/section navigation -->
          <div class="xl:hidden mb-4 pt-6 space-y-3">
            <!-- Tab selector -->
            <select id="mobile-tab"
              class="block rounded-md boredr-gray-300 shadow-sm focus:border-bluedark-100 focus:ring focus:ring-bluedark-100/30"
              :value="openTab" @change="onMobileTabChange($event)">
              <option v-for="(tab, idx) in tabs" :key="`mtab-${idx}`" :value="idx">{{ tab.text }}</option>
            </select>
          </div>
          <div v-if="assessmentSchema" class="flex">
            <aside class="hidden xl:block w-1/3 mb-4 lg:pl-8 pr-10">
              <!-- sticky card wrapper: gives border + white background and sticky behavior -->
              <div class="sticky top-8 bg-white border border-gray-100 rounded-md z-10">
                <nav aria-label="Områder">
                  <ul>
                    <li v-for="(tab, index) in tabs" :key="tab.value">
                      <button type="button" @click.prevent="toggleTabs(index)"
                        :aria-current="openTab === index ? 'page' : null"
                        class="w-full text-left text-xl p-5 border flex gap-3" :class="[
                          openTab === index
                            ? 'bg-bluedark-100 border-bluedark-100 text-white'
                            : 'border-gray-100 bg-white',
                          index === 0 ? 'rounded-t-md' : ''
                        ]">
                        <div class="flex justify-center items-center rounded-full border-2 w-8 h-8"
                          :class="[openTab === index ? 'border-white' : 'border-gray-200']">
                          <span class="text-lg font-semibold text-gray-700 block mt-1.5">{{ index + 1 }}</span>
                        </div>
                        <span class="block mt-1.5">
                          {{ tab.text }}
                        </span>
                      </button>
                      <div v-if="openTab === index && sectionMenuItems.length > 0"
                        class="py-4 bg-graylight-200 border border-gray-100">
                        <SectionMenu :sections="sectionMenuItems" :openSection="openSection"
                          @toggleSections="toggleSections($event)" :level="index + 1" />
                      </div>
                    </li>
                  </ul>
                </nav>
              </div>
            </aside>

            <div class="w-full xl:w-3/4 border px-10 pt-10 rounded-md bg-white">
              <div v-if="screenWidth < 1280">
                <div v-for="schemaItem in assessmentSchema.schemaItems.filter(si => si.fieldGroup === openTab)"
                  :key="schemaItem.contentGuid">
                  <template
                    v-if="schemaItem.blockType.toString().toLowerCase() === 'section' || schemaItem.blockType.toString().toLowerCase() === '0'">
                    <Section :model="schemaItem" :readOnly="readOnly" :preview="preview" />
                  </template>

                  <template
                    v-else-if="schemaItem.blockType.toString().toLowerCase() === 'peerreviewsection' || schemaItem.blockType.toString().toLowerCase() === '1'">
                    <PeerReviewSection v-if="!hasPeerReviewsV2" :model="schemaItem" :readOnly="readOnly"
                      :title="assessmentSchema.name" :organization="assessmentSchema.organization" :preview="preview"
                      :isPeerReviewed="isPeerReviewed" :isPeerReview="false" :isPublished="true" />
                    <PeerReviewSectionV2 v-else :model="model" :readOnly="readOnly"
                      :reviews="assessmentSchema.peerreviews" :organization="assessmentSchema.organization"
                      :isPublished="isPublished" :isPeerReviewed="isPeerReviewed" />
                  </template>
                </div>
              </div>

              <div v-else>
                <ul>
                  <li v-for="schemaItem in assessmentSchema.schemaItems" :key="schemaItem.contentGuid">
                    <template
                      v-if="schemaItem.fieldGroup === openTab &&
                        (schemaItem.blockType.toString().toLowerCase() === 'section' || schemaItem.blockType.toString().toLowerCase() === '0')">
                      <Section v-if="schemaItem.contentGuid === openSection" :model="schemaItem" :readOnly="readOnly"
                        :preview="preview" :isPublished="true" />
                    </template>

                    <template
                      v-if="schemaItem.fieldGroup === openTab &&
                        (schemaItem.blockType.toString().toLowerCase() === 'peerreviewsection' || schemaItem.blockType.toString().toLowerCase() === '1')">
                      <PeerReviewSection v-if="schemaItem.contentGuid === openSection && !hasPeerReviewsV2"
                        :model="schemaItem" :readOnly="readOnly" :title="assessmentSchema.name"
                        :organization="assessmentSchema.organization" :preview="preview"
                        :isPeerReviewed="isPeerReviewed" :isPublished="isPublished" :isPeerReview="true" />
                      <PeerReviewSectionV2 v-if="schemaItem.contentGuid === openSection && hasPeerReviewsV2"
                        :model="model" :readOnly="readOnly" :reviews="assessmentSchema.peerreviews"
                        :organization="assessmentSchema.organization" :isPublished="isPublished"
                        :isPeerReviewed="isPeerReviewed" />
                    </template>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ChangeLogSimple from '@/components/article/ChangeLogSimple.vue';
import Toolbar from '@/views/components/toolbar/Toolbar.vue';
import Section from '@/views/components/schema/Section.vue';
import Loading from '@/views/components/Loading.vue';
import PeerReviewSection from '@/views/components/schema/peerReview/PeerReviewSection.vue';
import PeerReviewSectionV2 from '@/views/components/schema/peerReview/PeerReviewSectionV2.vue';
import SectionMenu from '@/views/components/schema/SectionMenu.vue';
import SectionNavigation from '@/views/components/schema/SectionNavigation.vue';

export default {
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isMobile: false,
      screenWidth: 0,
      assessment: null,
      assessmentSchema: null,
      readOnly: true,
      preview: false,
      isPublished: true,
      isPeerReviewed: false,
      hasPeerReviewsV2: false,
      contactEmail: '',
      contactName: '',
      openTab: 0,
      openSection: '',
      tabs: [],
      expandedTabs: [],
      sectionMenuItems: [],
    };
  },
  components: {
    ChangeLogSimple,
    Toolbar,
    Section,
    Loading,
    PeerReviewSection,
    PeerReviewSectionV2,
    SectionMenu,
    SectionNavigation,
  },
  computed: {
    sectionsByTab() {
      const map = {};
      if (!this.assessmentSchema || !this.assessmentSchema.schemaItems) return map;

      this.tabs.forEach((_, idx) => { map[idx] = []; });

      this.assessmentSchema.schemaItems.forEach(item => {
        if (!item.hideWhenPublished && item.showInLeftMenu) {
          const idx = item.fieldGroup;
          if (!map[idx]) map[idx] = [];
          map[idx].push({ title: item.title, id: item.contentGuid });
        }
      });
      return map;
    },
  },
  mounted() {
    this.getAssessment();
    this.updateScreenWidth();
    window.addEventListener('resize', this.updateScreenWidth);
  },
  methods: {
    getAssessment() {
      const assessmentId = this.$route.query.id;

      fetch(`${window.location.origin}/api/methodassessment/GetAssessment?id=${assessmentId}`)
        .then(response => response.json())
        .then(data => {
          this.$store.commit('changeLastElementBreadcrumbs', { text: data.heading, url: '' });
          this.assessment = data;
          this.contactEmail = `mailto:${data.contactEmail}`;
          this.contactName = data.contactName;
          this.assessmentSchema = data.schema;
          this.isPublished = data.schema.isPublished;
          this.isPeerReviewed = data.schema.isPeerReviewed;
          this.hasPeerReviewsV2 = data.hasPeerReviewsV2;
          this.setupTabFields();
          const params = new URLSearchParams(window.location.search);
          const hasTabParam = params.has('tab');
          const hasSectionParam = params.has('section');

          const primary = this.assessmentSchema?.schemaItems?.find(
            item => item.isPrimarySection === true
          );
          if (hasTabParam && hasSectionParam) {
            this.openTab = Number(params.get('tab'));
            this.openSection = params.get('section');
          } else if (primary && this.isPublished) {
            this.openTab = primary.fieldGroup;
            this.openSection = primary.contentGuid;
          } else {
            this.openTab = 0;
            this.openSection = this.getFirstSectionId(this.openTab);
          }

          this.setSectionMenuItems();

          this.expandedTabs = [];
          this.expandedTabs[this.openTab] = true;

          this.setMetaTags();
        });
    },
    setMetaTags() {
      document.title = this.assessment.heading;
      document.querySelector('meta[property="og:title"]').setAttribute('content', this.assessment.heading);
    },
    toggleTabs(index) {
      this.openTab = index;
      this.openSection = this.getFirstSectionId(index);
      this.setSectionMenuItems();
      this.pushState();
      this.$nextTick(() => {
        window.scrollTo({ top: 500, behavior: 'smooth' });
      });
    },

    onMobileTabChange(e) {
      const idx = Number(e.target.value);
      const index = Number.isFinite(idx) ? idx : 0;
      this.toggleTabs(index);
    },
    toggleSections(id) {
      window.scrollTo({
        top: 500,
        left: 0,
        behavior: 'smooth',
      });
      this.openSection = id;
      this.pushState();
    },
    setSectionMenuItems() {
      this.sectionMenuItems = this.assessmentSchema.schemaItems.filter((item) => {
        if (!item.hideWhenPublished && item.showInLeftMenu && item.fieldGroup === this.openTab) {
          return {
            title: item.title,
            id: item.contentGuid,
          };
        }
        return false;
      });
    },
    getFirstSectionId(index) {
      if (!this.assessmentSchema?.schemaItems) return null;

      const items = this.assessmentSchema.schemaItems.filter(
        item => item.showInLeftMenu && item.fieldGroup === index && !item.hideWhenPublished
      );

      return items.length ? items[0].contentGuid : null;
    },
    pushState() {
      const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?id=${this.$route.query.id}&tab=${this.openTab}&section=${this.openSection}`;
      window.history.pushState({ path: newurl }, '', newurl);
    },
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    setupTabFields() {
      this.tabs = this.assessmentSchema.fieldGroups || [];
      this.expandedTabs[this.openTab ?? 0] = true;
    },
    toggleAccordion(index) {
      const isOpen = !!this.expandedTabs[index];
      if (isOpen) {
        this.expandedTabs[index] = false;
        return;
      }
      this.expandedTabs = [];
      this.expandedTabs[index] = true;

      if (this.openTab !== index) {
        this.openTab = index;
        this.openSection = this.getFirstSectionId(index);
        this.setSectionMenuItems();
        this.pushState();
      }
    },

    onSelectSection(index, sectionId) {
      if (this.openTab !== index) this.openTab = index;
      this.openSection = sectionId;
      this.setSectionMenuItems();
      this.pushState();
      window.scrollTo({ top: 500, left: 0, behavior: 'smooth' });
    },
  },
};
</script>
